import { inject, isDevMode } from '@angular/core';
import {
  HttpRequest,
  HttpHandlerFn,
  HttpEvent,
  HttpErrorResponse,
  HttpInterceptorFn
} from '@angular/common/http';
import { Observable, throwError, catchError } from 'rxjs';
import { JwtService } from '../services/jwt.service';

// Variable globale pour éviter les redirections multiples
let isRedirecting = false;

/** Reset redirect state (for testing) */
export function resetRedirectState(): void {
  isRedirecting = false;
}

/**
 * JWT Interceptor - Automatically attaches JWT token to outgoing requests
 *
 * SECURITY FEATURES:
 * - Excludes authentication endpoints from token attachment
 * - Handles 401/403 errors with automatic token cleanup
 * - Prevents multiple concurrent redirects
 * - Production-safe logging (no sensitive data in production)
 */
export const jwtInterceptor: HttpInterceptorFn = (
  request: HttpRequest<unknown>,
  next: HttpHandlerFn
): Observable<HttpEvent<unknown>> => {
  const jwtService = inject(JwtService);

  // Liste des URLs à exclure de l'interception
  const excludedUrls = [
    '/auth/login',
    '/auth/logout',
    '/auth/refresh',
  ];

  // Ne pas intercepter les requêtes d'authentification
  if (excludedUrls.some(url => request.url.includes(url))) {
    return next(request);
  }

  const token = jwtService.getToken();

  // Si pas de token, laisser passer la requête sans token
  // Le guard s'occupera de la redirection si nécessaire
  if (!token) {
    return next(request);
  }

  // Ajouter le token à la requête
  const modifiedRequest = request.clone({
    setHeaders: {
      Authorization: `Bearer ${token}`
    }
  });

  // Continuer avec la requête modifiée et gérer les erreurs
  return next(modifiedRequest).pipe(
    catchError((error: HttpErrorResponse) => {
      // Gestion spécifique des erreurs 401
      if (error.status === 401) {
        handle401Error(jwtService);
      }

      // Log errors only in development mode (without sensitive details)
      if (isDevMode()) {
        console.warn(`[JwtInterceptor] HTTP ${error.status} error on ${request.method} request`);
      }

      return throwError(() => error);
    })
  );
};

/**
 * Gère les erreurs 401 (Unauthorized)
 * @param jwtService JwtService
 */
function handle401Error(jwtService: JwtService): void {
  // Éviter les redirections multiples
  if (isRedirecting) {
    return;
  }

  isRedirecting = true;

  // Supprimer le token
  jwtService.removeToken();

  // Utiliser setTimeout pour éviter les problèmes de cycle de vie Angular
  setTimeout(() => {
    jwtService.redirectLoginPage();
    // Reset du flag après redirection
    setTimeout(() => {
      isRedirecting = false;
    }, 1000);
  }, 100);
}
